import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';

const standards = [
  {
    title: 'Zero Tolerance for CSAE',
    body: 'Kala Manch does not allow child sexual abuse material, sexual exploitation of minors, grooming, sextortion, trafficking, or any content or behavior that sexualizes or harms children.',
  },
  {
    title: 'No Unsafe Contact With Minors',
    body: 'Users may not use profiles, portfolio links, private messages, enquiries, or collaboration requests to solicit minors, request sexual content, arrange exploitative meetings, or move unsafe conversations off-platform.',
  },
  {
    title: 'Content and Message Moderation',
    body: 'Kala Manch may review profiles, images, videos, portfolio links, PMB messages, projects, competitions, live auditions, AI Studio usage, and support reports. Unsafe content may be blocked, hidden, removed, preserved for compliance, and escalated to the admin team.',
  },
  {
    title: 'Reporting and Response',
    body: 'Users can report child safety concerns in the app or website through support/contact flows. Reports are reviewed by the Kala Manch team, and urgent or credible CSAE concerns may be reported to appropriate authorities as required by law.',
  },
];

const ChildSafetyStandards = () => {
  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 pt-24 sm:pt-32">
      <SEO
        title="Child Safety Standards"
        description="Kala Manch child safety standards against child sexual abuse and exploitation, including reporting, moderation, and compliance contact information."
        keywords="Kala Manch child safety, CSAE policy, child protection standards, report safety concern"
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="uppercase tracking-[0.32em] text-sm font-semibold text-[#FF6F00] mb-5">Child Safety Standards</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
          Standards against child sexual abuse and exploitation.
        </h1>
        <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">
          Kalamanch.art is a creative discovery platform for artists and collaborators, owned and operated by Tivaro
          LLP. We do not allow content, messages, profiles, media, links, or conduct that exploits, endangers,
          sexualizes, or abuses children.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          {standards.map((item) => (
            <article key={item.title} className="rounded-[2rem] bg-white p-6 shadow-lg ring-1 ring-orange-100">
              <h2 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h2>
              <p className="text-gray-700 leading-relaxed">{item.body}</p>
            </article>
          ))}
        </div>

        <article className="rounded-[2rem] bg-white p-6 sm:p-8 shadow-lg ring-1 ring-orange-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Prohibited Content and Conduct</h2>
          <ul className="space-y-3 text-gray-700">
            <li>CSAM or any sexual content involving minors.</li>
            <li>Grooming, coercion, sextortion, trafficking, or attempts to exploit minors.</li>
            <li>Requests for sexual images, videos, meetings, projects, auditions, or work involving minors.</li>
            <li>Use of AI Studio, PMB, project posts, or live auditions to sexualize, exploit, contact, groom, or endanger minors.</li>
            <li>Links, coded language, or off-platform contact attempts used to evade child safety rules.</li>
            <li>Any content or conduct that violates applicable child safety laws and regulations.</li>
          </ul>
        </article>

        <div className="grid lg:grid-cols-[1fr_0.8fr] gap-8">
          <article className="rounded-[2rem] bg-white p-6 sm:p-8 shadow-lg ring-1 ring-orange-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Report</h2>
            <p className="text-gray-700 leading-relaxed mb-5">
              Report child safety concerns with the profile link, message details, screenshots if available, and a short
              description. Kala Manch reviews reports and may remove content, restrict accounts, preserve records, and
              report serious concerns to relevant authorities.
            </p>
            <Link to="/contact?subject=Child%20Safety%20Concern" className="btn-primary inline-flex">
              Report a Child Safety Concern
            </Link>
          </article>

          <aside className="rounded-[2rem] bg-gradient-to-br from-[#4A148C] to-[#FF6F00] p-6 sm:p-8 text-white shadow-xl">
            <h2 className="text-2xl font-bold mb-4">Compliance Contact</h2>
            <p className="text-white/90 leading-relaxed mb-4">
              Designated child safety contact for Kala Manch compliance and urgent child safety reports:
            </p>
            <a href="mailto:me@kalamanch.art" className="text-xl font-bold underline underline-offset-4">
              me@kalamanch.art
            </a>
          </aside>
        </div>

        <article className="rounded-[2rem] bg-white p-6 sm:p-8 shadow-lg ring-1 ring-orange-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Compliance</h2>
          <p className="text-gray-700 leading-relaxed">
            Tivaro LLP, as owner and operator of Kalamanch.art, maintains these standards under applicable child
            safety laws and may cooperate with regional, national, and law enforcement authorities where required.
            Users should not post or request unsafe minor-related content in profiles, messages, projects,
            competitions, auditions, AI Studio, or linked media. These standards are reviewed as our safety
            practices, app features, and legal requirements evolve.
          </p>
        </article>
      </section>
    </main>
  );
};

export default ChildSafetyStandards;
